import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/hooks/use-theme";
import { useEffect, useRef } from "react";
import AnalyticsService from "./services/analytics/AnalyticsService";

// Pages
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import LandingPage from "./pages/LandingPage";
import Locations from "./pages/Locations";
import Industries from "./pages/Industries";
import SpanishPage from "./pages/SpanishPage";
import About from "./pages/About";
import Products from "./pages/Products";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import Portfolio from "./pages/Portfolio";
import ProjectDetail from "./pages/ProjectDetail";
import Contact from "./pages/Contact";
// Payment page is intentionally unrouted until the Qatar licence is in place and
// payments go to the company account. Re-add this import and the /payment route
// below to restore it; src/pages/Payment.tsx is kept on disk for that purpose.
// import Payment from "./pages/Payment";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Terms from "./pages/Terms";
import WebDesign from "./pages/software/webdesign";
import AiWorkflowAutomation from "./pages/software/ai-workflow-automation";
import PropertyManagementPortals from "./pages/software/property-management-portals";
import SeoServices from "./pages/seo/seo-services";
import FAQ from "./pages/FAQ";
import KogitCaseStudy from "./pages/KogitCaseStudy";
import FreeWebsite from "./pages/FreeWebsite";
import AffordableWebsiteDesign from "./pages/AffordableWebsiteDesign";
import Testimonials from "./pages/Testimonials";
import GoogleAdsManagement from "./pages/GoogleAdsManagement";

// City Pages

// Components
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";

const queryClient = new QueryClient();

// Scroll to top on route change
const ScrollToTop = () => {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  return null;
};

/**
 * The prerendered HTML carries a canonical, robots tag and JSON-LD for the URL
 * it was built for, marked data-prerender. They must stay for the first page
 * (that is what crawlers render), but after a client-side navigation they
 * describe the wrong page, so drop them on the first route change.
 */
const RouteHeadCleanup = () => {
  const location = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    document.head.querySelectorAll('[data-prerender]').forEach((el) => el.remove());
  }, [location.pathname]);
  return null;
};

// Analytics wrapper component
const AnalyticsWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  
  useEffect(() => {
    // Start analytics tracking when the app loads
    const analyticsService = AnalyticsService.getInstance();
    analyticsService.startTracking();
    
    // Clean up on app unmount
    return () => {
      analyticsService.stopTracking();
    };
  }, []);
  
  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="dark">
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AnalyticsWrapper>
            <ScrollToTop />
            <RouteHeadCleanup />
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/services/:id" element={<ServiceDetail />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/portfolio" element={<Portfolio />} />
                  <Route path="/portfolio/:id" element={<ProjectDetail />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:id" element={<BlogPost />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/software/webdesign" element={<WebDesign />} />
                  <Route path="/software/ai-workflow-automation" element={<AiWorkflowAutomation />} />
                  <Route path="/software/property-management-portals" element={<PropertyManagementPortals />} />
                  <Route path="/seo/seo-services" element={<SeoServices />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/case-study/kogit" element={<KogitCaseStudy />} />
                  <Route path="/free-website" element={<FreeWebsite />} />
                  <Route path="/affordable-website-design" element={<AffordableWebsiteDesign />} />
                  <Route path="/testimonials" element={<Testimonials />} />
                  <Route path="/reviews" element={<Testimonials />} />
                  <Route path="/google-ads-management" element={<GoogleAdsManagement />} />
                  <Route path="/google-ads-management/:slug" element={<LandingPage kind="city-ads" />} />
                  <Route path="/ai-automation/:slug" element={<LandingPage kind="city-ai" />} />
                  <Route path="/industries" element={<Industries />} />
                  <Route path="/industries/:slug" element={<LandingPage kind="industry" />} />
                  <Route path="/locations" element={<Locations />} />
                  
                  {/* City Pages */}
                  <Route path="/es" element={<SpanishPage />} />
                  <Route path="/es/*" element={<SpanishPage />} />
                  {/* /city-<slug> web design pages, rendered from src/content/pages/city-web */}
                  <Route path="/:slug" element={<LandingPage kind="city-web" />} />
                  
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </main>
              <Footer />
            </div>
          </AnalyticsWrapper>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
